import { differenceInCalendarMonths } from 'date-fns';
import { useLayoutEffect, useRef, useState } from 'react';

import { DEFAULT_CANVAS_PADDING } from '../engine/scales';
import type { ChartV2Insight } from '../types/chart-v2.types';
import styles from './InsightsOverlay.module.scss';

const MAX_INSIGHTS_DOMAIN_MONTHS = 6;

export interface InsightsOverlayProps {
  insights: ChartV2Insight[];
  domain: [number, number];
  /**
   * Number of series in the pane — determines how much right-side space is
   * reserved for the y-axis columns so insight positions line up with the
   * canvas plot area.
   */
  seriesCount: number;
  /**
   * Width (px) of each reserved y-axis column. Shared with the canvas so
   * insight x-positions line up with the plot area.
   */
  axisColumnWidth: number;
  /**
   * Whether this pane owns the x-axis (a little more bottom padding is
   * reserved for the tick labels when true).
   */
  isXAxisPane?: boolean;
  zoomInToViewText?: string;
}

const X_AXIS_BAND_HEIGHT = 20;
const PLOT_BOTTOM_GAP = 10;

export const InsightsOverlay = ({
  insights,
  domain,
  seriesCount,
  axisColumnWidth,
  zoomInToViewText,
  isXAxisPane = false,
}: InsightsOverlayProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const node = rootRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];

      if (entry) {
        setWidth(entry.contentRect.width);
      }
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const domainExceedsMaxWindow =
    differenceInCalendarMonths(new Date(domain[1]), new Date(domain[0])) >
    MAX_INSIGHTS_DOMAIN_MONTHS;

  const plotLeft = DEFAULT_CANVAS_PADDING.left;
  const plotRight =
    width - DEFAULT_CANVAS_PADDING.right - seriesCount * axisColumnWidth;
  const plotWidth = Math.max(0, plotRight - plotLeft);
  const span = domain[1] - domain[0];
  const visible = domainExceedsMaxWindow
    ? []
    : insights.filter(i => i.time >= domain[0] && i.time <= domain[1]);

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-testid="chartv2-insights-overlay"
    >
      {domainExceedsMaxWindow && !!zoomInToViewText && (
        <p
          className={styles.zoomIntoViewText}
          data-testid="chartv2-zoom-in-required"
        >
          {zoomInToViewText}
        </p>
      )}
      {plotWidth > 0 &&
        visible.map(insight => {
          const xFraction = (insight.time - domain[0]) / span;
          const xPx = plotLeft + xFraction * plotWidth;

          const bottom = isXAxisPane
            ? X_AXIS_BAND_HEIGHT + PLOT_BOTTOM_GAP
            : PLOT_BOTTOM_GAP;

          return (
            <div
              key={insight.id}
              className={styles.insight}
              style={{ left: xPx, bottom }}
              data-testid={`chartv2-insight-${insight.id}`}
            >
              {insight.icons.map((icon, index) => (
                <div
                  key={index}
                  className={styles.icon}
                  data-testid={`chartv2-insight-${insight.id}-icon-${index}`}
                >
                  {icon}
                </div>
              ))}
            </div>
          );
        })}
    </div>
  );
};
